/**
 * 头像压缩工具（架构 §1.2 / §8）
 *  compressAvatar：选图 -> 等比缩放到最长边 <= AVATAR_MAX_PX -> JPEG 逐步降质到 <= AVATAR_MAX_BYTES
 *  返回 base64 dataURL，由后端 User.avatar 直接存储（MVP 方案）。
 */
import { AVATAR_MAX_PX, AVATAR_MAX_BYTES } from '../constants/profile';

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('读取文件失败'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片解析失败'));
    img.src = src;
  });
}

/**
 * 头像压缩：等比缩放到最长边 <= AVATAR_MAX_PX，再按 JPEG 逐步降质直到 <= AVATAR_MAX_BYTES。
 * 返回 base64 dataURL。
 */
export async function compressAvatar(file: File): Promise<string> {
  const dataUrl = await readFileAsDataURL(file);
  const img = await loadImage(dataUrl);
  const srcW = img.width || 1;
  const srcH = img.height || 1;
  const maxSide = Math.max(srcW, srcH);
  const scale = Math.min(1, AVATAR_MAX_PX / maxSide);
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, w, h);

  let quality = 0.92;
  let out = canvas.toDataURL('image/jpeg', quality);
  while (out.length > AVATAR_MAX_BYTES && quality > 0.3) {
    quality = Math.round((quality - 0.1) * 100) / 100;
    out = canvas.toDataURL('image/jpeg', quality);
  }
  return out;
}
